import { useCallback } from 'react';
import { useSigma, useCamera } from '@react-sigma/core';
import { useGraphStore } from '@/stores/graph';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Focus,
  Download,
  Loader2,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { NodeAttributes, EdgeAttributes } from './graphAdapter';

export function GraphToolbar() {
  const sigma = useSigma<NodeAttributes, EdgeAttributes>();
  const { zoomIn, zoomOut, reset } = useCamera();
  const { fullscreen, toggleFullscreen, vizLoading, snapshot, filters } = useGraphStore();

  const handleZoomIn = useCallback(() => zoomIn({ duration: 200 }), [zoomIn]);
  const handleZoomOut = useCallback(() => zoomOut({ duration: 200 }), [zoomOut]);
  const handleFitView = useCallback(() => reset({ duration: 400 }), [reset]);

  const handleExport = useCallback(() => {
    const canvas = sigma.getContainer().querySelector('canvas');
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `cognitive-graph-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [sigma]);

  return (
    <div className="absolute top-3 right-3 z-20 flex flex-col gap-1">
      <ToolbarButton onClick={handleZoomIn} title="Zoom in">
        <ZoomIn size={14} />
      </ToolbarButton>
      <ToolbarButton onClick={handleZoomOut} title="Zoom out">
        <ZoomOut size={14} />
      </ToolbarButton>
      <ToolbarButton onClick={handleFitView} title="Fit to view">
        <Focus size={14} />
      </ToolbarButton>
      <div className="h-px bg-slate-700/50 mx-1" />
      <ToolbarButton onClick={handleExport} title="Export as PNG">
        <Download size={14} />
      </ToolbarButton>
      <ToolbarButton onClick={toggleFullscreen} title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
        {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
      </ToolbarButton>

      {/* Status indicator */}
      {vizLoading && (
        <div className="mt-2 p-1.5 bg-slate-900/90 rounded-md flex items-center justify-center">
          <Loader2 size={14} className="animate-spin text-teal-400" />
        </div>
      )}

      {/* Layout badge */}
      {snapshot && !vizLoading && (
        <div className="mt-2">
          <div
            className={clsx(
              'px-2 py-1 rounded-md text-[9px] font-medium text-center backdrop-blur',
              filters.layout === 'embedding'
                ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/30'
                : 'bg-amber-600/20 text-amber-300 border border-amber-500/30',
            )}
          >
            {filters.layout === 'embedding' ? 'Embedding' : 'Force'}
          </div>
        </div>
      )}
    </div>
  );
}

function ToolbarButton({
  onClick,
  title,
  children,
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className="p-1.5 bg-slate-900/80 hover:bg-slate-700 text-slate-400 hover:text-white rounded-md backdrop-blur transition-colors border border-slate-700/30"
    >
      {children}
    </button>
  );
}
